import { type ReactNode, useEffect, useRef, useState } from 'react';
import { UptimeSelect } from '../../shared';
import { CRITICALITY_LEVELS, type PeriodPreset, SEARCH_FIELDS, type SearchField } from '../trackingUtils';
import type { TrackingStatus } from '../types';
import CriticalityBadge from './CriticalityBadge';
import useMtText from './useMtText';
import './TrackingToolbar.scss';

const STATUSES: TrackingStatus[] = ['open', 'finished', 'current'];

interface ITrackingToolbarProps {
    status: TrackingStatus;
    onStatusChange: (status: TrackingStatus) => void;
    searchField: SearchField;
    searchValue: string;
    onSearch: (term: string, field: SearchField) => void;
    criticality: string;
    onCriticalityChange: (value: string) => void;
    alertOnly: boolean;
    onAlertToggle: () => void;
    period?: PeriodPreset;
    onPeriodChange: (preset: PeriodPreset) => void;
    moreFiltersCount: number;
    onOpenMoreFilters: () => void;
    actions?: ReactNode;
}

const PERIODS: PeriodPreset[] = ['today', '7d', '30d', 'all'];

const TrackingToolbar = ({
    status,
    onStatusChange,
    searchField,
    searchValue,
    onSearch,
    criticality,
    onCriticalityChange,
    alertOnly,
    onAlertToggle,
    period,
    onPeriodChange,
    moreFiltersCount,
    onOpenMoreFilters,
    actions,
}: ITrackingToolbarProps) => {
    const { t, msg } = useMtText();
    const [query, setQuery] = useState(searchValue);
    const inputRef = useRef<HTMLInputElement>(null);
    const placeholderId = SEARCH_FIELDS.find(field => field.value === searchField)?.placeholderId;

    useEffect(() => setQuery(searchValue), [searchValue]);

    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;
            const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
            const overlayOpen = !!document.querySelector('.upt-drawer, .modal.show, .kanban-sidebar');
            if (event.key === '/' && !typing && !overlayOpen) {
                event.preventDefault();
                inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                inputRef.current?.focus({ preventScroll: true });
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, []);

    const submitSearch = () => {
        if (query.trim().toUpperCase() !== searchValue) {
            onSearch(query, searchField);
        }
    };

    return (
        <div className='mt-toolbar'>
            <fieldset className='mt-seg' aria-label={t('status.label')}>
                {STATUSES.map(item => (
                    <button
                        type='button'
                        key={item}
                        aria-pressed={status === item}
                        onClick={() => status !== item && onStatusChange(item)}
                    >
                        {t(`status.${item}`)}
                    </button>
                ))}
            </fieldset>

            <div className='mt-omni'>
                <UptimeSelect
                    className='mt-omni-field'
                    triggerClassName='mt-omni-trigger'
                    value={searchField}
                    options={SEARCH_FIELDS.map(field => ({ value: field.value, label: msg(field.labelId) }))}
                    onChange={(field: SearchField) => {
                        setQuery('');
                        onSearch('', field);
                    }}
                />
                <input
                    ref={inputRef}
                    type='text'
                    value={query}
                    placeholder={msg(placeholderId)}
                    aria-label={msg(placeholderId)}
                    autoComplete='off'
                    onChange={event => setQuery(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && submitSearch()}
                    onBlur={submitSearch}
                />
                <kbd title={t('search.shortcutHint')}>/</kbd>
            </div>

            <UptimeSelect
                label={msg('intl-msg:kanban.filterRow.criticality')}
                value={criticality}
                options={[
                    { value: '', label: msg('intl-msg:kanban.filterRow.allFeminine') },
                    ...CRITICALITY_LEVELS.map(level => ({
                        value: level.value,
                        label: <CriticalityBadge value={level.value} />,
                    })),
                ]}
                onChange={onCriticalityChange}
            />

            <button
                type='button'
                className={`mt-btn${alertOnly ? ' mt-on' : ''}`}
                aria-pressed={alertOnly}
                onClick={onAlertToggle}
            >
                {msg('intl-msg:kanban.filterRow.inAlert')}
            </button>

            <fieldset className='mt-seg' aria-label={t('period.label')}>
                {PERIODS.map(preset => (
                    <button
                        type='button'
                        key={preset}
                        aria-pressed={period === preset}
                        onClick={() => onPeriodChange(preset)}
                    >
                        {t(`period.${preset}`)}
                    </button>
                ))}
            </fieldset>

            <button type='button' className='mt-btn' onClick={onOpenMoreFilters}>
                {t('filters.more')}
                {moreFiltersCount > 0 && <span className='mt-badge'>{moreFiltersCount}</span>}
            </button>

            <span className='mt-spacer' />

            {actions}
        </div>
    );
};

export default TrackingToolbar;
